import { Component, Input, Output, EventEmitter, OnChanges, SimpleChanges, OnDestroy } from '@angular/core';
import { Habit, HabitCompletion } from './challenge.model';

@Component({
    selector: 'app-weekly-goal-card',
    templateUrl: './weekly-goal-card.component.html',
    styleUrls: ['./weekly-goal-card.component.scss'],
    standalone: false
})
export class WeeklyGoalCardComponent implements OnChanges, OnDestroy {
  @Input() habit!: Habit;
  @Input() weekCompletions: HabitCompletion[] = [];
  // Plumbed from parent; reserved for highlighting today's day-pill in a future iteration.
  @Input() today: string = '';

  @Output() toggle = new EventEmitter<void>();
  @Output() removeCompletion = new EventEmitter<string>();
  @Output() noteChange = new EventEmitter<{ completionId: string, note: string }>();
  @Output() editHabit = new EventEmitter<void>();

  noteExpandedFor: string | null = null; // completion id
  draftNoteFor: { [completionId: string]: string } = {};
  private noteTimeouts: { [completionId: string]: ReturnType<typeof setTimeout> } = {};

  get target(): number { return this.habit.target ?? 1; }
  get progressPercent(): number { return Math.min(100, (this.weekCompletions.length / this.target) * 100); }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['weekCompletions']) {
      const next: { [completionId: string]: string } = {};
      for (const c of this.weekCompletions) {
        next[c.id] = this.noteTimeouts[c.id]
          ? (this.draftNoteFor[c.id] ?? c.note ?? '')
          : (c.note ?? '');
      }
      this.draftNoteFor = next;
    }
  }

  ngOnDestroy(): void {
    for (const completionId of Object.keys(this.noteTimeouts)) {
      clearTimeout(this.noteTimeouts[completionId]);
      this.noteChange.emit({ completionId, note: this.draftNoteFor[completionId] ?? '' });
    }
  }

  weekdayLabel(date: string): string {
    const [y, m, d] = date.split('-').map(Number);
    return new Date(y, m - 1, d).toLocaleDateString('en-US', { weekday: 'short' });
  }

  doToggle() { this.toggle.emit(); }
  doEdit(ev: Event) { ev.stopPropagation(); this.editHabit.emit(); }

  doRemove(completionId: string) {
    if (this.noteTimeouts[completionId]) {
      clearTimeout(this.noteTimeouts[completionId]);
      delete this.noteTimeouts[completionId];
    }
    if (this.noteExpandedFor === completionId) this.noteExpandedFor = null;
    this.removeCompletion.emit(completionId);
  }

  expandNote(completionId: string) { this.noteExpandedFor = completionId; }

  isExpanded(completionId: string): boolean {
    return this.noteExpandedFor === completionId || !!(this.draftNoteFor[completionId]?.trim());
  }

  onNoteInput(completionId: string, value: string) {
    this.draftNoteFor[completionId] = value;
    if (this.noteTimeouts[completionId]) clearTimeout(this.noteTimeouts[completionId]);
    this.noteTimeouts[completionId] = setTimeout(() => {
      this.noteChange.emit({ completionId, note: value });
      delete this.noteTimeouts[completionId];
    }, 500);
  }

  onNoteBlur(completionId: string) {
    if (this.noteTimeouts[completionId]) { clearTimeout(this.noteTimeouts[completionId]); delete this.noteTimeouts[completionId]; }
    this.noteChange.emit({ completionId, note: this.draftNoteFor[completionId] ?? '' });
  }
}
